import * as Sentry from "@sentry/browser";
import {DOM} from "../DOM";
import {Notify} from "../Notify";
import {INetworkingCreateRequestParameters} from "../../definitions/utils/networking/INetworkingCreateRequestParameters";
import {SentryUtilCleanURI, SentryUtilGetClient, SentryUtilGetEnvironment} from "./SentryUtil";
import {Toggles} from "../Toggles";
import {Attachment} from "@sentry/types/build/types/attachment";

export const SentryInit = () => {

    if (!Toggles.Handler.toggleAvailable('SENTRY')) { return; }

    const sentrySessionClient = SentryUtilGetClient();
    const sentryEnv = SentryUtilGetEnvironment();

    // noinspection JSUnusedGlobalSymbols
    Sentry.init({
        dsn: sentryEnv === 5
            ? "https://8fd3ba36d98bbef5b9a2430e2985473c@o381139.ingest.sentry.io/4506677631385600"
            : "https://f7f47fdb3493caed396934d08506b23b@o381139.ingest.sentry.io/4506666009755648",
        release: DOM.Parser.getDataByNameFromElement('release', DOM.Parser.getBodyElement()),
        environment: sentryEnv === 5  ? 'production' : 'sentryTest',
        initialScope: {
            user: {id: sentrySessionClient ? sentrySessionClient.id : undefined},
            extra: {sessionClient: sentrySessionClient}
        },
        integrations: [
            Sentry.browserTracingIntegration({}),
            Sentry.browserProfilingIntegration(),
            Sentry.replayIntegration({
                blockAllMedia: true,
                unmask: ['.sentry-unmask, [data-sentry-unmask]'],
                networkDetailAllowUrls: [window.location.origin],
                networkRequestHeaders: ['X-Custom-Header'],
                networkResponseHeaders: ['X-Custom-Header'],
            }),
        ],
        tracesSampleRate: sentryEnv === 5 ? 0.2 : 1,
        profilesSampleRate: sentryEnv === 5 ? 0.5 : 1,
        replaysSessionSampleRate: sentryEnv === 5 ? 0.01 : 1,
        replaysOnErrorSampleRate: sentryEnv === 5 ? 1.0 : 1,
        beforeSend: async (event, hint) => {
            // CISV3-JS-6: Report ticket attachments
            if (window.location.pathname.includes('tickets.tickets')) {
                if (document.getElementById('ticketReplyForm')) {
                    hint.attachments = await SentryReportFileAttachment(
                        DOM.Parser.getHtmlElement('ticketReplyForm') as HTMLFormElement,
                        'attachment'
                    ) ?? undefined;
                }
            }
            return event;
        },
    });
}

// Send attachment to Sentry using form and input name
export const SentryReportFileAttachment = (formElement: HTMLFormElement, inputName: string): Promise<Attachment[]|null> => {

    return new Promise((resolve) => {
        const file = (new FormData(formElement)).get(inputName) as File;
        if (file.size === 0) { resolve(null); }
        const reader = new FileReader();
        reader.onload =  () => {
            if (reader.result) {

                const arrayBuffer = reader.result as ArrayBuffer;
                const array = new Uint8Array(arrayBuffer);

                resolve([{
                    filename: file.name,
                    contentType: file.type,
                    attachmentType: "event.attachment",
                    data: array
                }]);
            }
        }
        reader.readAsArrayBuffer(file);
    })
}

// Report error on Networking XHR wrong HTTP code returned
export const SentryReportNetworkingXHR = (xhr: XMLHttpRequest, requestParameters: INetworkingCreateRequestParameters) => {
    const urlClean = SentryUtilCleanURI(requestParameters.url);
    const statusText = xhr.statusText.trim().length > 0 ? xhr.statusText : xhr.status;
    const sentryId = Sentry.captureMessage(`[NET-XHR]: ${statusText} - ${requestParameters.type}/${urlClean}`,
        {extra: {
            statusText: xhr.statusText,
            status: xhr.status,
            readyState: xhr.readyState,
            requestParameters: requestParameters,
            responseType:  xhr.responseType,
            response: xhr.responseText,
            timeout: xhr.timeout,
        }});
    Notify.Handler.showServerError(sentryId, ['JS', 'INT', 'NET', 'XHR', statusText.toString()]);
    console.error(`[NET-XHR]: ${statusText} - ${requestParameters.type}/${urlClean}`);
}

// Report error on Networking XHR timeout occurred
export const SentryReportNetworkingXHRTimeout = (xhr: XMLHttpRequest, requestParameters: INetworkingCreateRequestParameters) => {
    const urlClean = SentryUtilCleanURI(requestParameters.url);
    const sentryId = Sentry.captureMessage(`[NET-XHR-TIMEOUT]: Timeout - ${requestParameters.type}/${urlClean}`,
        {extra: {response: xhr.responseText, status: xhr.statusText, url: requestParameters.url}});
    Notify.Handler.showServerError(sentryId, ['JS', 'INT', 'NET', 'XHR', 'TIMEOUT']);
    console.error(`[NET-XHR-TIMEOUT]: Timeout - ${requestParameters.type}/${urlClean}`);
}

// Report error on Networking response parse
export const SentryReportNetworkingParseException = (xhr: XMLHttpRequest, e: any, requestParameters: INetworkingCreateRequestParameters) => {
    const sentryId = Sentry.captureException(e, {extra: {response: xhr.responseText, status: xhr.statusText, url: requestParameters.url}})
    Notify.Handler.showServerError(sentryId, ['JS', 'INT', 'NET', 'PRS']);
    console.error(`[NET-PRS]: ${e.message.replace(/(\r\n|\n|\r)/gm, "")}`, e);
}

// Report custom message to Sentry
export const SentryReportCustomError = (msg: string, extra?: object) => {
    Sentry.captureMessage(msg, {extra: extra ? {...{url: window.location.href}, ...extra} : {url: window.location.href}});
    console.error(msg, extra);
}

// Send feedback from report feedback control
export const SentryReportUserFeedback = (message: string, sentryEventId: string|null) : string => {

    const sentrySessionClient = SentryUtilGetClient();
    const eventId = sentryEventId ?? Sentry.captureMessage("User Feedback");
    Sentry.captureFeedback({
        associatedEventId: eventId,
        name: sentrySessionClient?.name ?? 'Anonymous user',
        email: sentrySessionClient?.email ?? 'anonymous@master.cz',
        message: message,
        url: window.location.href
    });

    return eventId;
}